import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import {
	horseAPI,
	productAPI,
	transportAPI,
	schoolAPI,
	stableAPI,
	trainerAPI,
	eventAPI,
	vetAPI,
	favoriteAPI,
	authAPI,
	gdprAPI,
	invoiceAPI,
	verificationAPI,
	paymentAPI,
	savedSearchAPI,
	mediaAPI
} from '@/services/api';
import {
	Edit, Trash2, Heart, Star, ShoppingBag, Download, AlertTriangle,
	Eye, Shield, FileText, Receipt, CreditCard, Power, PowerOff, Bookmark,
	Image as ImageIcon, ExternalLink, Building2, Truck, GraduationCap, Home, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import VerifyEmailBanner from '@/components/VerifyEmailBanner';
import { toast } from 'sonner';
import { resolveSearchType, resolveUrl } from "@/utils/adUtil";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

const LANGUAGES = [
	{ code: 'en', label: 'English', flag: 'EN' },
	{ code: 'pt', label: 'Portugues', flag: 'PT' },
	{ code: 'es', label: 'Espanol', flag: 'ES' },
	{ code: 'fr', label: 'Francais', flag: 'FR' },
	{ code: 'de', label: 'Deutsch', flag: 'DE' },
	{ code: 'pl', label: 'Polski', flag: 'PL' },
];

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTab = searchParams.get('tab') || 'horses';
	const setActiveTab = (tab) => {
		const next = new URLSearchParams(searchParams);
		if (tab && tab !== 'horses') next.set('tab', tab); else next.delete('tab');
		setSearchParams(next, { replace: true });
	};
	// Build a "from" hint so detail/edit pages can render a proper back-link
	const buildFrom = (tab, label) =>
		({ path: `/dashboard${tab && tab !== 'horses' ? `?tab=${tab}` : ''}`, label });
  const [myHorses, setMyHorses] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
	const [myTransports, setMyTransports] = useState([]);
	const [mySchools, setMySchools] = useState([]);
	const [myStables, setMyStables] = useState([]);
	const [myTrainers, setMyTrainers] = useState([]);
	const [myEvents, setMyEvents] = useState([]);
	const [myVets, setMyVets] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [invoices, setInvoices] = useState([]);
	const [savedSearches, setSavedSearches] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
	const [profileForm, setProfileForm] = useState({
		name: '', phone: '', bio: '', location: '', company_name: '', lang: ''
	});
	const [coverUploading, setCoverUploading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    setProfileForm({
			name: user.name || '',
			phone: user.phone || '',
			bio: user.bio || '',
			location: user.location || '',
			company_name: user.company_name || '',
			lang: user.lang || i18n.language
		});
    loadData();
  }, [user]);

	const mergeAds = (ads, pendingAds) => {
		const publishedAds = ads.map((ad) => ({
			...ad,
			pendingPayment: pendingAds.filter(pend => ad.id === pend.id).length > 0,
		}));
		const publishedSet = new Set(ads.map((ad) => ad.id));
		const unpublishedADs = pendingAds.filter(pend => !publishedSet.has(pend.id)).map((pend) => ({
			...pend,
			pendingPayment: true,
		}))
		return [...publishedAds, ...unpublishedADs];
	}

  const loadData = async () => {
    setLoading(true);
    try {
      const [
				h,
				ph,
				p,
				pp,
				t,
				pt,
				rs,
				prs,
				s,
				ps,
				trainersPromise,
				pendingTrainersPromise,
				eventsPromise,
				pendingEventsPromise,
				vetsPromise,
				pendingVetsPromise,
				f,
				inv,
				ver,
				ss
			] = await Promise.all([
        horseAPI.myAds(),
				horseAPI.myPendingAds(),
				productAPI.myAds(),
				productAPI.myPendingAds(),
				transportAPI.myAds(),
				transportAPI.myPendingAds(),
				schoolAPI.myAds(),
				schoolAPI.myPendingAds(),
				stableAPI.myAds(),
				stableAPI.myPendingAds(),
				trainerAPI.myAds(),
				trainerAPI.myPendingAds(),
				eventAPI.myAds(),
				eventAPI.myPendingAds(),
				vetAPI.myAds(),
				vetAPI.myPendingAds(),
				favoriteAPI.list(),
        invoiceAPI.list().catch(() => ({ data: [] })),
        verificationAPI.status().catch(() => ({ data: null })),
				savedSearchAPI.list().catch(() => ({ data: [] }))
      ]);

			const horses = h.data || [];
			const pendingHorses = ph.data || [];
			setMyHorses(mergeAds(horses, pendingHorses));
			const products = p.data || [];
			const pendingProducts = pp.data || [];
			setMyProducts(mergeAds(products, pendingProducts));
			const transports = t.data || [];
			const pendingTransports = pt.data || [];
			setMyTransports(mergeAds(transports, pendingTransports));
			const schools = rs.data || [];
			const pendingSchools = prs.data || [];
			setMySchools(mergeAds(schools, pendingSchools));
			const stables = s.data || [];
			const pendingStables = ps.data || [];
			setMyStables(mergeAds(stables, pendingStables));
			const trainers = trainersPromise.data || [];
			const pendingTrainers = pendingTrainersPromise.data || [];
			setMyTrainers(mergeAds(trainers, pendingTrainers));
			const events = eventsPromise.data || [];
			const pendingEvents = pendingEventsPromise.data || [];
			setMyEvents(mergeAds(events, pendingEvents));
			const vets = vetsPromise.data || [];
			const pendingVets = pendingVetsPromise.data || [];
			setMyVets(mergeAds(vets, pendingVets));
			setFavorites(f.data || []);
      setInvoices(inv.data || []);
      setVerificationStatus(ver.data);
			setSavedSearches(ss.data || []);
    } catch {}
    setLoading(false);
  };

  const saveProfile = async () => {
    try {
      const res = await authAPI.updateProfile(profileForm);
      updateUser(res.data);
      toast.success(t('common.success'));
    } catch { toast.error(t('common.error')); }
  };

	const reactivateHorse = async (id) => {
		try {
			await horseAPI.reactivate(id);
			loadData();
			toast.success(t('dashboard.ad_reactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deactivateHorse = async (id) => {
		try {
			await horseAPI.deactivate(id);
			loadData();
			toast.success(t('dashboard.ad_deactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deactivateProduct = async (id) => {
		try {
			await productAPI.deactivate(id);
			loadData();
			toast.success(t('dashboard.ad_deactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const reactivateProduct = async (id) => {
		try {
			await productAPI.reactivate(id);
			loadData();
			toast.success(t('dashboard.ad_reactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deactivateTransport = async (id) => {
		try {
			await transportAPI.deactivate(id);
			loadData();
			toast.success(t('dashboard.ad_deactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const reactivateTransport = async (id) => {
		try {
			await transportAPI.reactivate(id);
			loadData();
			toast.success(t('dashboard.ad_reactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deactivateSchool = async (id) => {
		try {
			await schoolAPI.deactivate(id);
			loadData();
			toast.success(t('dashboard.ad_deactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const reactivateSchool = async (id) => {
		try {
			await schoolAPI.reactivate(id);
			loadData();
			toast.success(t('dashboard.ad_reactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deactivateStable = async (id) => {
		try {
			await stableAPI.deactivate(id);
			loadData();
			toast.success(t('dashboard.ad_deactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const reactivateStable = async (id) => {
		try {
			await stableAPI.reactivate(id);
			loadData();
			toast.success(t('dashboard.ad_reactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deactivateTrainer = async (id) => {
		try {
			await trainerAPI.deactivate(id);
			loadData();
			toast.success(t('dashboard.ad_deactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const reactivateTrainer = async (id) => {
		try {
			await trainerAPI.reactivate(id);
			loadData();
			toast.success(t('dashboard.ad_reactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deactivateEvent = async (id) => {
		try {
			await eventAPI.deactivate(id);
			loadData();
			toast.success(t('dashboard.ad_deactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const reactivateEvent = async (id) => {
		try {
			await eventAPI.reactivate(id);
			loadData();
			toast.success(t('dashboard.ad_reactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deactivateVet = async (id) => {
		try {
			await vetAPI.deactivate(id);
			loadData();
			toast.success(t('dashboard.ad_deactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const reactivateVet = async (id) => {
		try {
			await vetAPI.reactivate(id);
			loadData();
			toast.success(t('dashboard.ad_reactivated'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const completePayment = async (adId) => {
		setLoading(true);
		try {
			const res = await paymentAPI.complete(adId);
			window.location.href = res.data.url;
		} catch (err) {
			toast.error(err.response?.data?.detail || t('payment.failed'));
			setLoading(false);
		}
	}

	const deletePendingHorseAd = async (id) => {
		try {
			await horseAPI.deletePending(id);
			loadData();
			toast.success(t('dashboard.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingProductAd = async (id) => {
		try {
			await productAPI.deletePending(id);
			loadData();
			toast.success(t('dashboard.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingTransportAd = async (id) => {
		try {
			await transportAPI.deletePending(id);
			loadData();
			toast.success(t('dashboard.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingSchoolAd = async (id) => {
		try {
			await schoolAPI.deletePending(id);
			loadData();
			toast.success(t('dashboard.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingStableAd = async (id) => {
		try {
			await stableAPI.deletePending(id);
			loadData();
			toast.success(t('dashboard.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingTrainerAd = async (id) => {
		try {
			await trainerAPI.deletePending(id);
			loadData();
			toast.success(t('dashboard.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingEventAd = async (id) => {
		try {
			await eventAPI.deletePending(id);
			loadData();
			toast.success(t('dashboard.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const deletePendingVetAd = async (id) => {
		try {
			await vetAPI.deletePending(id);
			loadData();
			toast.success(t('dashboard.ad_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const removeFav = async (adId) => {
		try {
			await favoriteAPI.remove(adId);
			loadData();
			toast.success(t('common.removed_fav'));
		} catch {
		}
	};

	const deleteSavedSearch = async (id) => {
		if (!window.confirm(t('savedSearch.confirm_delete'))) return;
		try {
			await savedSearchAPI.remove(id);
			setSavedSearches((prev) => prev.filter((s) => s.id !== id));
			toast.success(t('common.success'));
		} catch { toast.error(t('common.error')); }
	};

	const uploadCoverImage = async (e) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file) return;
		if (file.size > 10 * 1024 * 1024) { toast.error(t('common.error')); return; }
		setCoverUploading(true);
		try {
			const fd = new FormData();
			fd.append('file', file);
			const res = await mediaAPI.uploadCover(fd);
			updateUser({ ...user, cover_image: res.data.url });
			toast.success(t('common.success'));
		} catch { toast.error(t('common.error')); }
		setCoverUploading(false);
	};

	const updateSavedSearchAlert = async (id, frequency) => {
		try {
			const res = await savedSearchAPI.update(id, { alert_frequency: frequency });
			setSavedSearches((prev) => prev.map((s) => (s.id === id ? { ...s, alert_frequency: res.data.alert_frequency } : s)));
			toast.success(t('common.success'));
		} catch { toast.error(t('common.error')); }
	};

  const exportData = async () => {
    try {
      const res = await gdprAPI.exportData();
      const blob = new Blob(
				[JSON.stringify(res.data, null, 2)],
				{ type: 'application/json' }
			);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'equimarket_data_export.json';
      a.click();
			toast.success(t('dashboard.data_exported'));
    } catch { toast.error(t('common.error')); }
  };

  const exportFullData = async () => {
    try {
      const res = await gdprAPI.exportFull();
      const blob = new Blob(
				[JSON.stringify(res.data, null, 2)],
				{ type: 'application/json' }
			);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'equimarket_full_export.json';
      a.click();
			toast.success(t('dashboard.full_data_exported'));
    } catch { toast.error(t('common.error')); }
  };

	const anonymizeAccount = async () => {
		if (!window.confirm(t('common.anonymization_confirmation'))) return;
		try {
			await gdprAPI.anonymize();
			toast.success(t('dashboard.account_anonymized'));
			logout();
			navigate('/');
		} catch {
			toast.error(t('common.error'));
		}
	};

	const downloadInvoice = async (invoiceId, invoiceNumber) => {
		try {
			const res = await invoiceAPI.downloadPdf(invoiceId);
			const url = URL.createObjectURL(res.data);
			const a = document.createElement('a');
			a.href = url;
			a.download = `${ invoiceNumber }.pdf`;
			a.click();
		} catch {
			toast.error(t('dashboard.invoice_download_error'));
		}
	};

	const deleteAccount = async () => {
		try {
			await gdprAPI.deleteAccount();
			logout();
			navigate('/');
			toast.success(t('dashboard.account_deleted'));
		} catch {
			toast.error(t('common.error'));
		}
	};

  if (!user) return null;

  return (
    <div className="pt-20 md:pt-24 min-h-screen" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<VerifyEmailBanner />
				<div className="flex items-center justify-between mb-8 mt-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-medium text-[#1A3C34] font-['Playfair_Display']">
							{t('dashboard.title')}
						</h1>
            <div className="flex flex-col md:flex-row md:items-center gap-3 mt-1">
              <p className="text-[#666055]">{ t('dashboard.welcome') }, {user.name}</p>
              {user.is_verified ? (
                <span className="inline-flex items-start md:items-center gap-1 text-xs text-[#8B4513] bg-[#8B4513]/10 px-2 py-0.5
                		rounded-sm font-medium">
                  <Shield className="w-3 h-3" /> { t('verification.verified_seller') }
                </span>
              ) : (
                <Link to="/verification"
											className="inline-flex items-start md:items-center gap-1 text-xs text-[#1A3C34] bg-[#F2F0E9] px-2 py-0.5
													rounded-sm font-medium hover:bg-[#E6E2D6] transition-colors"
											data-testid="get-verified-link">
                  <Shield className="w-3 h-3" /> { t('dashboard.get_verified') }
                </Link>
              )}
            </div>
          </div>
          <div className="flex gap-2">
						{ user.email_verified &&
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<button
										data-testid="nav-sell-btn"
										className="px-4 py-2 text-sm font-medium rounded-sm transition-colors flex items-center
												gap-1 text-white bg-[#1A3C34] hover:bg-[#153029]" >
										{t('nav.sell')} <ChevronDown className="w-3 h-3" />
									</button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end" className="bg-white border-[#E6E2D6]">
									<DropdownMenuItem onClick={() => navigate('/sell/horse')}
																		data-testid="sell-horse-option">
										{t('horse.create_ad')}
									</DropdownMenuItem>
									<DropdownMenuItem onClick={() => navigate('/sell/product')}
																		data-testid="sell-product-option">
										{t('product.create_ad')}
									</DropdownMenuItem>
									<DropdownMenuItem onClick={() => navigate('/sell/transport')}
																		data-testid="sell-transport-option">
										{t('transport.create_ad')}
									</DropdownMenuItem>
									<DropdownMenuItem onClick={() => navigate('/sell/school')}
																		data-testid="sell-school-option">
										{t('school.create_title')}
									</DropdownMenuItem>
									<DropdownMenuItem onClick={() => navigate('/sell/stable')}
																		data-testid="sell-stable-option">
										{t('stable.create_title')}
									</DropdownMenuItem>
									<DropdownMenuItem onClick={() => navigate('/sell/trainer')}
																		data-testid="sell-trainer-option">
										{t('trainer.create_title')}
									</DropdownMenuItem>
									<DropdownMenuItem onClick={() => navigate('/sell/event')}
																		data-testid="sell-event-option">
										{t('event.create_title')}
									</DropdownMenuItem>
									<DropdownMenuItem onClick={() => navigate('/sell/vet')}
																		data-testid="sell-vet-option">
										{t('vet.create_title')}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
					<TabsList className="bg-white border border-[#E6E2D6] p-1 rounded-sm h-auto flex flex-nowrap justify-start
							md:flex-wrap md:justify-center overflow-x-auto md:overflow-visible gap-1 -mx-4 px-4 md:mx-0 md:px-1 scrollbar-hide">
            <TabsTrigger value="horses" data-testid="tab-horses"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		gap-2 px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('dashboard.my_horses')} ({myHorses.length})
						</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		gap-2 px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('dashboard.my_products')} ({myProducts.length})
						</TabsTrigger>
						<TabsTrigger value="transports" data-testid="tab-transports"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		gap-2 px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('dashboard.my_transports')} ({myTransports.length})
						</TabsTrigger>
						<TabsTrigger value="schools" data-testid="tab-schools"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		gap-2 px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('dashboard.my_riding_schools')} ({mySchools.length})
						</TabsTrigger>
						<TabsTrigger value="stables" data-testid="tab-stables"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		gap-2 px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('dashboard.my_stables')} ({myStables.length})
						</TabsTrigger>
						<TabsTrigger value="trainers" data-testid="tab-stables"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		gap-2 px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('dashboard.my_trainers')} ({myTrainers.length})
						</TabsTrigger>
						<TabsTrigger value="events" data-testid="tab-stables"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		gap-2 px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('dashboard.my_events')} ({myEvents.length})
						</TabsTrigger>
						<TabsTrigger value="vets" data-testid="tab-stables"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		gap-2 px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('dashboard.my_vets')} ({myVets.length})
						</TabsTrigger>
            <TabsTrigger value="favorites" data-testid="tab-favorites"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		gap-2 px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('dashboard.my_favorites')} ({favorites.length})
						</TabsTrigger>
						<TabsTrigger value="saved-searches" data-testid="tab-saved-searches"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		gap-2 px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('savedSearch.tab_title')} ({savedSearches.length})
						</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		gap-2 px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('dashboard.settings')}
						</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices"
												 className="shrink-0 rounded-sm data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white
												 		gap-2 px-3 py-1.5 text-sm md:px-6 md:py-2 md:text-base whitespace-nowrap">
							{t('dashboard.invoices')} ({invoices.length})
						</TabsTrigger>
          </TabsList>

          <TabsContent value="horses">
            {myHorses.length === 0 ? (
              <EmptyState icon={Star} text={t('dashboard.no_ads')} />
            ) : (
              <div className="space-y-3" data-testid="my-horses-list">
								<div className="w-full text-sm">
									{ myHorses.map(ad => (
										<AdRow key={ ad.id } ad={ ad }
													 onEdit={ () => navigate(`/edit/horse/${ ad.id }`,
														 { state: { from: buildFrom('horses', t('dashboard.my_horses')) } }) }
													 onClone={ () => navigate(`/clone/horse/${ ad.id }`,
														 { state: { from: buildFrom('horses', t('dashboard.my_horses')) } }) }
													 onDeactivate={ () => deactivateHorse(ad.id) }
													 onReactivate={ () => reactivateHorse(ad.id) }
													 onCompletePayment={ () => completePayment(ad.id) }
													 onDeletePendingAd={ () => deletePendingHorseAd(ad.id) }
													 t={ t }/>
									)) }
								</div>
							</div>
            )}
          </TabsContent>

          <TabsContent value="products">
            {myProducts.length === 0 ? (
              <EmptyState icon={ShoppingBag} text={t('dashboard.no_ads')} />
            ) : (
              <div className="space-y-3" data-testid="my-products-list">
                {myProducts.map(ad => (
									<AdRow key={ ad.id } ad={ ad }
												 onEdit={ () => navigate(`/edit/product/${ ad.id }`,
														{ state: { from: buildFrom('products', t('dashboard.my_products')) } }) }
												 onClone={ () => navigate(`/clone/product/${ ad.id }`,
													 	{ state: { from: buildFrom('products', t('dashboard.my_products')) } }) }
												 onDeactivate={ () => deactivateProduct(ad.id) }
												 onReactivate={ () => reactivateProduct(ad.id) }
												 onCompletePayment={ () => completePayment(ad.id) }
												 onDeletePendingAd={ () => deletePendingProductAd(ad.id) }
												 t={ t }/>
                ))}
              </div>
            )}
          </TabsContent>

					<TabsContent value="transports">
						{myTransports.length === 0 ? (
							<EmptyState icon={Truck} text={t('dashboard.no_ads')} />
						) : (
							<div className="space-y-3" data-testid="my-transports-list">
								{myTransports.map(ad => (
									<AdRow key={ ad.id } ad={ ad }
												 onEdit={ () => navigate(`/edit/transport/${ ad.id }`,
													 { state: { from: buildFrom('transports', t('dashboard.my_transports')) } }) }
												 onClone={ () => navigate(`/clone/transport/${ ad.id }`,
													 { state: { from: buildFrom('transports', t('dashboard.my_transports')) } }) }
												 onDeactivate={ () => deactivateTransport(ad.id) }
												 onReactivate={ () => reactivateTransport(ad.id) }
												 onCompletePayment={ () => completePayment(ad.id) }
												 onDeletePendingAd={ () => deletePendingTransportAd(ad.id) }
												 t={ t }/>
								))}
							</div>
						)}
					</TabsContent>

					<TabsContent value="schools">
						{mySchools.length === 0 ? (
							<EmptyState icon={GraduationCap} text={t('dashboard.no_ads')} />
						) : (
							<div className="space-y-3" data-testid="my-schools-list">
								{mySchools.map(ad => (
									<AdRow key={ ad.id } ad={ ad }
												 onEdit={ () => navigate(`/edit/school/${ ad.id }`,
													{ state: { from: buildFrom('schools', t('dashboard.my_riding_schools')) } }) }
												 onClone={ () => navigate(`/clone/school/${ ad.id }`,
													 { state: { from: buildFrom('schools', t('dashboard.my_riding_schools')) } }) }
												 onDeactivate={ () => deactivateSchool(ad.id) }
												 onReactivate={ () => reactivateSchool(ad.id) }
												 onCompletePayment={ () => completePayment(ad.id) }
												 onDeletePendingAd={ () => deletePendingSchoolAd(ad.id) }
												 t={ t }/>
								))}
							</div>
						)}
					</TabsContent>

					<TabsContent value="stables">
						{myStables.length === 0 ? (
							<EmptyState icon={Home} text={t('dashboard.no_ads')} />
						) : (
							<div className="space-y-3" data-testid="my-stables-list">
								{myStables.map(ad => (
									<AdRow key={ ad.id } ad={ ad }
												 onEdit={ () => navigate(`/edit/stable/${ ad.id }`,
														{ state: { from: buildFrom('stables', t('dashboard.my_stables')) } }) }
												 onClone={ () => navigate(`/clone/stable/${ ad.id }`,
													 	{ state: { from: buildFrom('stables', t('dashboard.my_stables')) } }) }
												 onDeactivate={ () => deactivateStable(ad.id) }
												 onReactivate={ () => reactivateStable(ad.id) }
												 onCompletePayment={ () => completePayment(ad.id) }
												 onDeletePendingAd={ () => deletePendingStableAd(ad.id) }
												 t={ t }/>
								))}
							</div>
						)}
					</TabsContent>

					<TabsContent value="trainers">
						{myTrainers.length === 0 ? (
							<EmptyState icon={Home} text={t('dashboard.no_ads')} />
						) : (
							<div className="space-y-3" data-testid="my-trainers-list">
								{myTrainers.map(ad => (
									<AdRow key={ ad.id } ad={ ad }
												 onEdit={ () => navigate(`/edit/trainer/${ ad.id }`,
														{ state: { from: buildFrom('trainers', t('dashboard.my_trainers')) } }) }
												 onClone={ () => navigate(`/clone/trainer/${ ad.id }`,
													 { state: { from: buildFrom('trainers', t('dashboard.my_trainers')) } }) }
												 onDeactivate={ () => deactivateTrainer(ad.id) }
												 onReactivate={ () => reactivateTrainer(ad.id) }
												 onCompletePayment={ () => completePayment(ad.id) }
												 onDeletePendingAd={ () => deletePendingTrainerAd(ad.id) }
												 t={ t }/>
								))}
							</div>
						)}
					</TabsContent>

					<TabsContent value="events">
						{myEvents.length === 0 ? (
							<EmptyState icon={Home} text={t('dashboard.no_ads')} />
						) : (
							<div className="space-y-3" data-testid="my-events-list">
								{myEvents.map(ad => (
									<AdRow key={ ad.id } ad={ ad }
												 onEdit={ () => navigate(`/edit/event/${ ad.id }`,
													 { state: { from: buildFrom('events', t('dashboard.my_events')) } }) }
												 onClone={ () => navigate(`/clone/event/${ ad.id }`,
													 { state: { from: buildFrom('events', t('dashboard.my_events')) } }) }
												 onDeactivate={ () => deactivateEvent(ad.id) }
												 onReactivate={ () => reactivateEvent(ad.id) }
												 onCompletePayment={ () => completePayment(ad.id) }
												 onDeletePendingAd={ () => deletePendingEventAd(ad.id) }
												 t={ t }/>
								))}
							</div>
						)}
					</TabsContent>

					<TabsContent value="vets">
						{myVets.length === 0 ? (
							<EmptyState icon={Home} text={t('dashboard.no_ads')} />
						) : (
							<div className="space-y-3" data-testid="my-vets-list">
								{myVets.map(ad => (
									<AdRow key={ ad.id } ad={ ad }
												 onEdit={ () => navigate(`/edit/vet/${ ad.id }`,
													 { state: { from: buildFrom('vets', t('dashboard.my_vets')) } }) }
												 onClone={ () => navigate(`/clone/vet/${ ad.id }`,
													 { state: { from: buildFrom('vets', t('dashboard.my_vets')) } }) }
												 onDeactivate={ () => deactivateVet(ad.id) }
												 onReactivate={ () => reactivateVet(ad.id) }
												 onCompletePayment={ () => completePayment(ad.id) }
												 onDeletePendingAd={ () => deletePendingVetAd(ad.id) }
												 t={ t }/>
								))}
							</div>
						)}
					</TabsContent>

          <TabsContent value="favorites">
            {favorites.length === 0 ? (
              <EmptyState icon={Heart} text="No favorites yet" />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-list">
                {favorites.map(ad => (
                  <div key={ad.id} className="bg-white border border-[#E6E2D6] rounded-sm overflow-hidden group">
                    <Link to={`/${ resolveUrl(ad.ad_type) }/${ad.slug || ad.id}`}
													state={{ from: buildFrom('favorites', t('dashboard.my_favorites')) }}>
                      <div className="aspect-[4/3] bg-[#F2F0E9] flex items-center justify-center">
                        {ad.images?.[0] ? (
                          <img src={ad.images[0].startsWith('http') ? ad.images[0] : `${process.env.REACT_APP_BACKEND_URL}${ad.images[0]}`}
															 alt={ad.title} className="w-full h-full object-cover" />
                        ) : <Star className="w-8 h-8 text-[#D4C5A9]" />}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-[#1A3C34] line-clamp-1 font-['Playfair_Display']">
													{ad.title}
												</h3>
                        <p className="text-lg font-bold text-[#8B4513]">&euro;{ad.price?.toLocaleString()}</p>
                      </div>
                    </Link>
                    <div className="px-4 pb-4">
                      <Button onClick={() => removeFav(ad.id)} variant="outline" size="sm"
															className="w-full border-[#E6E2D6] text-[#9B2C2C] rounded-sm text-xs">
                        <Heart className="w-3 h-3 mr-1" /> Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

					<TabsContent value="saved-searches">
						{savedSearches.length === 0 ? (
							<EmptyState icon={Bookmark} text={t('savedSearch.empty')} />
						) : (
							<div className="space-y-3" data-testid="saved-searches-list">
								{savedSearches.map((s) => {
									const typeLabel = t(resolveSearchType(s.ad_type));
									const created = s.created_at ? new Date(s.created_at).toLocaleDateString() : '';
									return (
										<div
											key={s.id}
											className="bg-white border border-[#E6E2D6] rounded-sm p-4 flex flex-col sm:flex-row sm:items-center
													sm:justify-between gap-4 hover:border-[#8B4513] transition-colors"
											data-testid={`saved-search-row-${s.id}`}
										>
											<div className="flex items-start gap-3 min-w-0 flex-1">
												<div className="w-10 h-10 rounded-sm bg-[#F2F0E9] flex items-center justify-center flex-shrink-0">
													<Bookmark className="w-5 h-5 text-[#8B4513]" />
												</div>
												<div className="min-w-0 flex-1">
													<h3 className="text-base font-semibold text-[#1A3C34] truncate font-['Playfair_Display']">
														{s.name}
													</h3>
													<div className="flex items-center gap-2 text-xs text-[#666055] mt-1 flex-wrap">
														<span className="bg-[#F2F0E9] px-2 py-0.5 rounded-sm uppercase tracking-wider font-semibold">
															{typeLabel}
														</span>
														{ created &&
															<span>{t('savedSearch.created')} {created}</span>
														}
													</div>
													{s.label && <p className="text-xs text-[#666055]/80 mt-1 truncate">{s.label}</p>}
													<div className="flex items-center gap-2 mt-2 flex-wrap">
														<span className="text-[10px] font-semibold text-[#666055] uppercase tracking-wider">
															{t('savedSearch.alert_frequency')}:
														</span>
														<Select
															value={s.alert_frequency || 'off'}
															onValueChange={(v) => updateSavedSearchAlert(s.id, v)}
														>
															<SelectTrigger className="h-7 text-xs bg-[#F9F7F2] border-[#E6E2D6] rounded-sm w-[120px]"
																						 data-testid={`saved-search-freq-${s.id}`}>
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																<SelectItem value="off">{t('savedSearch.freq_off')}</SelectItem>
																<SelectItem value="daily">{t('savedSearch.freq_daily')}</SelectItem>
																<SelectItem value="weekly">{t('savedSearch.freq_weekly')}</SelectItem>
															</SelectContent>
														</Select>
													</div>
												</div>
											</div>
											<div className="flex items-center gap-2 flex-shrink-0 sm:justify-end">
												<Link to={s.path} className="flex-1 sm:flex-none" data-testid={`saved-search-view-${s.id}`}>
													<Button size="sm" className="w-full sm:w-auto bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-9 text-xs">
														<Eye className="w-3 h-3 mr-1" /> {t('savedSearch.view')}
													</Button>
												</Link>
												<Button
													size="sm"
													variant="outline"
													onClick={() => deleteSavedSearch(s.id)}
													className="flex-1 sm:flex-none border-[#E6E2D6] text-[#9B2C2C] hover:bg-[#FBEAEA] rounded-sm h-9 text-xs"
													data-testid={`saved-search-delete-${s.id}`}
												>
													<Trash2 className="w-3 h-3 mr-1" /> {t('savedSearch.delete')}
												</Button>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</TabsContent>

          <TabsContent value="settings">
            <div className="space-y-6" data-testid="profile-settings">
							{/* Public Profile Section */}
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6 space-y-4">
								<div className="flex items-center justify-between gap-3 flex-wrap">
									<h3 className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display']">
										{t('seller.public_profile')}
									</h3>
									{user?.slug && (
										<Link
											to={`/u/${user.slug}`}
											target="_blank"
											rel="noopener"
											className="inline-flex items-center gap-1 text-xs text-[#8B4513] hover:text-[#6F3610] font-medium"
											data-testid="view-public-profile-link"
										>
											<ExternalLink className="w-3 h-3" /> {t('seller.view_profile')}
										</Link>
									)}
								</div>
								<p className="text-xs text-[#666055]">{t('seller.public_profile_help')}</p>

								{/* Cover image */}
								<div>
									<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
										{t('seller.cover_image')}
									</Label>
									<div className="mt-2 relative aspect-[16/4] bg-gradient-to-br from-[#1A3C34] via-[#2A5048]
											to-[#8B4513] rounded-sm overflow-hidden border border-[#E6E2D6]">
										{user?.cover_image ? (
											<img
												src={user.cover_image.startsWith('http') ? user.cover_image : `${process.env.REACT_APP_BACKEND_URL}${user.cover_image}`}
												alt=""
												className="w-full h-full object-cover"
												data-testid="profile-cover-preview"
											/>
										) : (
											<div className="w-full h-full flex items-center justify-center text-white/60">
												<ImageIcon className="w-8 h-8" />
											</div>
										)}
									</div>
									<label className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E6E2D6]
											rounded-sm text-sm text-[#1A3C34] hover:border-[#8B4513] cursor-pointer transition-colors"
												 data-testid="upload-cover-btn">
										<ImageIcon className="w-4 h-4 text-[#8B4513]" />
										{coverUploading ? t('common.loading') : t('seller.upload_cover')}
										<input type="file" accept="image/*" className="hidden" onChange={uploadCoverImage}
													 disabled={coverUploading} data-testid="cover-file-input" />
									</label>
									<p className="text-[10px] text-[#666055]/80 mt-1">{t('seller.cover_help')}</p>
								</div>

								<div>
									<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider flex items-center gap-1">
										<Building2 className="w-3 h-3" /> {t('seller.company_name')}
									</Label>
									<Input
										value={profileForm.company_name}
										onChange={(e) => setProfileForm({ ...profileForm, company_name: e.target.value })}
										placeholder={t('seller.company_placeholder')}
										className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
										data-testid="profile-company"
									/>
								</div>
							</div>

              <div className="bg-white border border-[#E6E2D6] rounded-sm p-6 space-y-4">
                <h3 className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display']">
									{t('dashboard.profile_settings')}
								</h3>
                <div>
                  <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
										{t('auth.name')}
									</Label>
                  <Input value={profileForm.name}
												 onChange={e => setProfileForm({...profileForm, name: e.target.value})}
												 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
												 data-testid="profile-name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
										{t('auth.phone')}
									</Label>
                  <Input value={profileForm.phone}
												 onChange={e => setProfileForm({...profileForm, phone: e.target.value})}
												 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
												 data-testid="profile-phone" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
										{t('horse.location')}
									</Label>
                  <Input value={profileForm.location}
												 onChange={e => setProfileForm({...profileForm, location: e.target.value})}
												 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
												 data-testid="profile-location" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
										{t('dashboard.bio')}
									</Label>
                  <Textarea value={profileForm.bio}
														onChange={e => setProfileForm({...profileForm, bio: e.target.value})}
														className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm"
														data-testid="profile-bio" />
                </div>
								<div>
									<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
										{t('admin.faq_languages')}
									</Label>
									{/* Language Selector */}
									<Select value={profileForm.lang} onValueChange={ e => setProfileForm({...profileForm, lang: e}) }>
										<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-10">
											<SelectValue placeholder="lang" />
										</SelectTrigger>
										<SelectContent>
											{ LANGUAGES.map(l =>
												<SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)
											}
										</SelectContent>
									</Select>
								</div>
                <Button onClick={saveProfile}
												className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-10 px-8"
												data-testid="save-profile-btn">
                  {t('dashboard.save_changes')}
                </Button>
              </div>

              {/* GDPR Section */}
              <div className="bg-white border border-[#E6E2D6] rounded-sm p-6 space-y-4">
                <h3 className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display']">
									{t('dashboard.data_privacy')}
								</h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={exportData} variant="outline" className="border-[#E6E2D6] rounded-sm h-10"
													data-testid="export-data-btn">
                    <Download className="w-4 h-4 mr-2" /> {t('dashboard.export_data')}
                  </Button>
                  <Button onClick={exportFullData} variant="outline" className="border-[#E6E2D6] rounded-sm h-10"
													data-testid="export-full-data-btn">
                    <FileText className="w-4 h-4 mr-2" />  {t('dashboard.full_export')}
                  </Button>
                </div>
                <div className="divider-gold" />
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800 mb-1">
												{t('dashboard.anonymize')}
											</p>
                      <p className="text-xs text-[#666055] mb-3">
												{t('dashboard.anonymize_message')}
											</p>
                      <Button variant="outline" size="sm"
															className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-100"
															onClick={anonymizeAccount} data-testid="anonymize-account-btn">
												{t('dashboard.anonymize_data')}
											</Button>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-red-50 border border-red-200 rounded-sm">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-[#9B2C2C] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[#9B2C2C] mb-1">
												{t('dashboard.delete_account')}
											</p>
                      <p className="text-xs text-[#666055] mb-3">
												{t('dashboard.delete_confirm')}
											</p>
                      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                        <DialogTrigger asChild>
                          <Button variant="destructive" size="sm" className="rounded-sm"
																	data-testid="delete-account-btn">
														{t('dashboard.delete_account')}
													</Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white">
                          <DialogHeader>
														<DialogTitle className="font-['Playfair_Display']">
															{t('dashboard.delete_confirm')}
														</DialogTitle>
													</DialogHeader>
                          <div className="flex gap-2 justify-end mt-4">
                            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}
																		className="rounded-sm">
															{t('common.cancel')}
														</Button>
                            <Button variant="destructive" onClick={deleteAccount} className="rounded-sm"
																		data-testid="confirm-delete-btn">
															{t('common.confirm')}
														</Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

					<TabsContent value="invoices">
						{invoices.length === 0 ? (
							<EmptyState icon={Receipt} text="No invoices yet" />
						) : (
							<div className="space-y-3" data-testid="invoices-list">
								{invoices.map(inv => (
									<div key={inv.id}
											 className="bg-white border border-[#E6E2D6] rounded-sm p-4 flex flex-col sm:flex-row sm:items-center
								gap-4 hover:shadow-sm transition-shadow"
											 data-testid={`invoice-${inv.id}`}>
										<div className="flex gap-4 min-w-0 flex-1">
											<div className="w-12 h-12 bg-[#F2F0E9] rounded-sm flex items-center justify-center shrink-0">
												<FileText className="w-6 h-6 text-[#8B4513]" />
											</div>
											<div className="flex-1 min-w-0">
												<h3 className="font-semibold text-[#1A3C34]">{inv.invoice_number}</h3>
												<p className="text-sm text-[#666055]">{inv.description}</p>
												<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#666055] mt-1">
													<span className="font-bold text-[#8B4513]">&euro;{inv.amount?.toFixed(2)}</span>
													<span>{t('admin.vat_percent')}: &euro;{inv.vat_amount?.toFixed(2)}</span>
													<span>{new Date(inv.created_at).toLocaleDateString()}</span>
												</div>
											</div>
										</div>
										<Button onClick={() => downloadInvoice(inv.id, inv.invoice_number)}
														variant="outline" size="sm"
														className="w-full sm:w-auto border-[#E6E2D6] rounded-sm h-8 text-xs shrink-0"
														data-testid={`download-invoice-${inv.id}`}>
											<Download className="w-3 h-3 mr-1" /> PDF
										</Button>
									</div>
								))}
							</div>
						)}
					</TabsContent>
				</Tabs>
      </div>
    </div>
  );
}

function AdRow({ ad, onEdit, onClone, onDeactivate, onReactivate, onCompletePayment, onDeletePendingAd, t }) {
	return (
		<div className="bg-white border border-[#E6E2D6] rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-4 hover:shadow-sm transition-shadow"
				 data-testid={`ad-row-${ad.id}`}>
			<div className="flex gap-4 min-w-0 flex-1">
				<div className="w-20 h-20 bg-[#F2F0E9] rounded-sm overflow-hidden shrink-0">
					{
						ad.images?.[0] ? (
							<img src={ad.images[0].startsWith('http') ? ad.images[0] : `${process.env.REACT_APP_BACKEND_URL}${ad.images[0]}`}
									 alt={ad.title} className="w-full h-full object-cover" />
						) : <div className="w-full h-full flex items-center justify-center">
							<Star className="w-6 h-6 text-[#D4C5A9]" />
						</div>
					}
				</div>
				<div className="flex-1 min-w-0">
					<h3 className="font-semibold text-[#1A3C34] line-clamp-1">{ad.title}</h3>
					<p className="text-lg font-bold text-[#8B4513]">&euro;{ad.price?.toLocaleString()}</p>
					<div className="flex items-center gap-3 text-xs text-[#666055] mt-1">
						<Badge variant={ad.status === 'active' ? 'default' : 'secondary'}
									 className={`text-[10px] rounded-sm ${ad.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
							{ad.status === 'active' ? t('dashboard.active') : t('dashboard.inactive')}
						</Badge>
						<span className="flex items-center gap-1">
							<Eye className="w-3 h-3" />{ad.views || 0}
						</span>
						{	ad.is_featured &&
							<span className="featured-badge px-2 py-0.5 text-[10px] font-bold">
								{t('horse.featured')}
							</span>
						}
					</div>
				</div>
			</div>
			<div className="flex flex-wrap sm:flex-nowrap gap-2 sm:shrink-0 sm:justify-end">
				{ad.status === 'active' && (
					<Button onClick={onEdit} variant="outline" size="sm" className="border-[#E6E2D6] rounded-sm h-8 text-xs">
						<Edit className="w-3 h-3 mr-1" />
						{t('dashboard.edit')}
					</Button>
				)}
				{(ad.status === 'expired' || ad.status === 'admin_inactive') && (
					<Button onClick={onClone} variant="outline" size="sm" className="border-[#E6E2D6] rounded-sm h-8 text-xs">
						<Edit className="w-3 h-3 mr-1" />
						{t('dashboard.repost')}
					</Button>
				)}
				{ad.pendingPayment &&
					<Button onClick={onCompletePayment} variant="outline" size="sm"
									className="border-[#E6E2D6] rounded-sm h-8 text-xs">
						<CreditCard className="w-3 h-3 mr-1" />
						{t('verification.complete_payment_title')}
					</Button>
				}
				{ad.status === 'active' && (
					<Button onClick={ onDeactivate } variant="outline" size="sm"
									className="border-[#E6E2D6] text-[#9B2C2C] rounded-sm h-8 text-xs">
						<PowerOff	className="w-3 h-3 mr-1"/>
						{ t('dashboard.deactivate') }
					</Button>
				)}
				{ ad.status === 'inactive' && (
					<Button onClick={ onReactivate } variant="outline" size="sm"
									className="rounded-sm h-7 text-xs border-[#E6E2D6] text-green-700"
									data-testid={ `dashboard-activate-${ ad.id }` }>
						<Power className="w-3 h-3 mr-1"/>
						{ t('dashboard.activate') }
					</Button>
				)}
				{ad.pendingPayment &&
					<Button onClick={onDeletePendingAd} variant="outline" size="sm"
									className="border-[#E6E2D6] rounded-sm h-8 text-xs text-[#9B2C2C]">
						<Trash2 className="w-3 h-3 mr-1" />
						{t('common.delete')}
					</Button>
				}
			</div>
		</div>
	);
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="text-center py-16">
      <Icon className="w-12 h-12 mx-auto mb-4 text-[#D4C5A9]" />
      <p className="text-[#666055]">{text}</p>
    </div>
  );
}
